import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * useFeatureSettings — resolved org+user feature settings.
 *
 * Returns the effective values (org defaults with the user's overrides
 * layered on) plus a `patchMine` to set a personal override. Cached on
 * window so the many widgets that need this don't each fire their own
 * request on every profile open; the cache is invalidated on any successful
 * patch so a change is reflected immediately across mounted widgets.
 */

const CACHE_KEY = '__banyakFeatureSettings';

async function fetchResolved() {
  const res = await fetch(`${API_BASE_URL}/api/feature-settings`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
  });
  if (!res.ok) throw new Error('settings');
  // If the route isn't deployed the host may serve index.html with a 200,
  // so verify it's actually JSON before parsing. Widgets that use this hook
  // fall back to their built-in defaults when it isn't.
  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) throw new Error('settings-unavailable');
  return res.json();
}

export default function useFeatureSettings() {
  const [settings, setSettings] = useState(() => window[CACHE_KEY]?.data || null);
  const [loading, setLoading] = useState(!window[CACHE_KEY]?.data);

  useEffect(() => {
    let alive = true;
    // Serve cache, but still refresh in the background so a long-open tab
    // doesn't drift from a setting an admin just changed.
    if (!window[CACHE_KEY]?.promise) {
      window[CACHE_KEY] = { ...(window[CACHE_KEY] || {}), promise: fetchResolved() };
    }
    window[CACHE_KEY].promise
      .then(data => {
        window[CACHE_KEY] = { data };
        if (alive) { setSettings(data); setLoading(false); }
      })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Set a personal override. Pass a key as null to clear it (revert to org).
  const patchMine = async (patch) => {
    const res = await fetch(`${API_BASE_URL}/api/feature-settings/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error('save');
    const data = await res.json();
    window[CACHE_KEY] = { data };   // invalidate + refill
    setSettings(data);
    return data;
  };

  return { settings, loading, patchMine };
}
